import type { UsageSeriesList } from '../../types';
import { AggregationType, Granularity } from '../../types';

type GetSeriesTotalProps = {
  series: UsageSeriesList['series'] | null | undefined;
  aggregationType: AggregationType;
  granularity: Granularity;
  metadataIdx?: string;
};

export function getSeriesTotal({
  series,
  aggregationType,
  granularity,
  metadataIdx = '0',
}: GetSeriesTotalProps): number {
  if (!series?.length) {
    return 0;
  }

  // Average: callers query with monthly granularity so the backend returns a
  // single pre-computed weighted average. Just return that value directly.
  if (aggregationType === AggregationType.AVERAGE) {
    const lastPoint = series[series.length - 1];
    return lastPoint?.[metadataIdx] ?? 0;
  }

  // Sum the values for incremental aggregation or monthly granularity.
  if (aggregationType === AggregationType.INCREMENTAL || granularity === Granularity.MONTHLY) {
    return series.reduce((acc, point) => {
      const value = point?.[metadataIdx];
      return acc + (value ?? 0);
    }, 0);
  }

  // Otherwise, return the last non-zero value from the most recent month in the series.
  const lastPoint = series[series.length - 1];
  if (!lastPoint?.time) {
    return 0;
  }

  const lastDate = new Date(lastPoint.time);
  const lastMonth = lastDate.getUTCMonth();
  const lastYear = lastDate.getUTCFullYear();

  for (let i = series.length - 1; i >= 0; i--) {
    const point = series[i];
    if (!point?.time) {
      continue;
    }

    const pointDate = new Date(point.time);

    // Stop if we've exited the last month
    if (
      pointDate.getUTCFullYear() < lastYear ||
      (pointDate.getUTCFullYear() === lastYear && pointDate.getUTCMonth() < lastMonth)
    ) {
      break;
    }

    const value = point[metadataIdx];
    if (!!value) {
      return value;
    }
  }

  return 0;
}
